import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FileButton,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPhoto, IconUpload } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';

import { trpc } from '../../../../../services/trpc';
import { useUploadThing } from '../../../../../services/upload';
import { AttachmentCard } from './-attachment-card';
import { ImageLightbox } from './-image-lightbox';

export const Route = createFileRoute('/app/projets/$slug/photos/')({
  component: RouteComponent,
});

const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
];

function RouteComponent() {
  const { slug } = Route.useParams();
  const [lightboxImage, setLightboxImage] = useState<{
    url: string;
    fileName: string;
  } | null>(null);

  const { data: project } = trpc.projects.get.useQuery({ slug });
  const { data: attachments, refetch } = trpc.attachments.list.useQuery(
    {
      projectId: project?.id || '',
    },
    { enabled: !!project?.id },
  );

  const uploadAttachment = trpc.attachments.upload.useMutation();
  const deleteAttachment = trpc.attachments.delete.useMutation();

  const { startUpload, isUploading } = useUploadThing('attachmentUploader');

  const handleUpload = async (files: File[]) => {
    if (!files || files.length === 0 || !project) return;

    try {
      const uploadResult = await startUpload(files);
      if (!uploadResult) {
        throw new Error('Upload failed');
      }

      // Save each file to database
      await Promise.all(
        uploadResult.map(file =>
          uploadAttachment.mutateAsync({
            projectId: project.id,
            utFileKey: file.key,
            fileName: file.name,
            mimeType:
              files.find(f => f.name === file.name)?.type ||
              'application/octet-stream',
          }),
        ),
      );

      notifications.show({
        title: 'Fichiers téléchargés',
        message: `${files.length} fichier(s) téléchargé(s) avec succès`,
        color: 'green',
      });

      refetch();
    } catch {
      notifications.show({
        title: 'Erreur',
        message: 'Échec du téléchargement des fichiers',
        color: 'red',
      });
    }
  };

  const handleDelete = (id: string) => {
    modals.openConfirmModal({
      title: 'Confirmer la suppression',
      children: (
        <Text size="sm">
          Êtes-vous sûr de vouloir supprimer cette pièce jointe ?
        </Text>
      ),
      labels: { confirm: 'Supprimer', cancel: 'Annuler' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteAttachment.mutateAsync({ id });
          notifications.show({
            title: 'Pièce jointe supprimée',
            message: 'La pièce jointe a été supprimée avec succès',
            color: 'green',
          });
          refetch();
        } catch {
          notifications.show({
            title: 'Erreur',
            message: 'Échec de la suppression de la pièce jointe',
            color: 'red',
          });
        }
      },
    });
  };

  const images =
    attachments?.filter(
      a =>
        IMAGE_MIME_TYPES.includes(a.mimeType) ||
        /\.(jpg|jpeg|png|gif|webp)$/i.test(a.fileName),
    ) || [];
  const documents =
    attachments?.filter(
      a =>
        !(
          IMAGE_MIME_TYPES.includes(a.mimeType) ||
          /\.(jpg|jpeg|png|gif|webp)$/i.test(a.fileName)
        ),
    ) || [];

  return (
    <Stack p="md" gap="xl">
      <Group justify="space-between" align="center">
        <Title order={2}>Pièces jointes</Title>
        <FileButton onChange={handleUpload} multiple>
          {props => (
            <Button
              {...props}
              leftSection={<IconUpload size={16} />}
              loading={isUploading}
            >
              Ajouter des fichiers
            </Button>
          )}
        </FileButton>
      </Group>

      {images.length > 0 && (
        <Stack gap="md">
          <Group gap="xs">
            <IconPhoto size={20} />
            <Title order={3}>Photos</Title>
            <Text size="sm" c="dimmed">
              ({images.length})
            </Text>
          </Group>
          <SimpleGrid
            cols={{ base: 2, sm: 3, md: 4, lg: 5 }}
            spacing="md"
            verticalSpacing="md"
          >
            {images.map(attachment => (
              <AttachmentCard
                key={attachment.id}
                id={attachment.id}
                fileName={attachment.fileName}
                utFileKey={attachment.utFileKey}
                isImage={true}
                onDelete={handleDelete}
                onImageClick={(utFileKey, fileName) =>
                  setLightboxImage({
                    url: `https://utfs.io/f/${utFileKey}`,
                    fileName,
                  })
                }
              />
            ))}
          </SimpleGrid>
        </Stack>
      )}

      {images.length > 0 && documents.length > 0 && <Divider />}

      {documents.length > 0 && (
        <Stack gap="md">
          <Title order={3}>Documents</Title>
          <Box style={{ maxWidth: '800px' }}>
            <Stack gap="sm">
              {documents.map(attachment => (
                <AttachmentCard
                  key={attachment.id}
                  id={attachment.id}
                  fileName={attachment.fileName}
                  utFileKey={attachment.utFileKey}
                  isImage={false}
                  onDelete={handleDelete}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      )}

      {(!attachments || attachments.length === 0) && (
        <Box
          style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: 'var(--mantine-color-dimmed)',
          }}
        >
          <Text size="lg" fw={500}>
            Aucune pièce jointe
          </Text>
          <Text size="sm" mt="xs">
            Cliquez sur le bouton ci-dessus pour télécharger des fichiers
          </Text>
        </Box>
      )}

      {lightboxImage && (
        <ImageLightbox
          opened={!!lightboxImage}
          onClose={() => setLightboxImage(null)}
          imageUrl={lightboxImage.url}
          fileName={lightboxImage.fileName}
        />
      )}
    </Stack>
  );
}
